import { useCallback, useMemo, useState } from "react";
import type { FloorBouquetInStock, FloorSellableItem } from "../../api/shopMode";
import type { CreateSaleLinePayload, DefaultUom } from "../../api/types";
import type { TranslateFn } from "../../i18n/t";
import { lineCents } from "../../utils/paymentSplit";
import { bouquetFloor, type BouquetFloor, type BouquetFloorLine } from "./kioskCostDisplay";
import { uomLabel } from "./shopFloorFormat";

/**
 * The floor ticket — lifted out of `ShopFloorSalePanel` (G-147) so the till
 * and "Customer collected"'s "+ Add items" share one cart: the same adding,
 * quantity caps, removal at zero, prices and rounding. Never copy it.
 */
export type CartLine =
  | {
      uid: number;
      kind: "BOUQUET";
      bouquetBuildId: string;
      label: string;
      priceStr: string;
      /** G-141: the price the till suggested, shown beside the one typed. */
      suggestedStr: string | null;
      /** G-45 / WO-B (OD-198): the floor to warn against, or "cannot check". */
      floor: BouquetFloor;
    }
  | {
      uid: number;
      kind: "ITEM";
      itemId: string;
      label: string;
      categoryCode: string;
      categoryLabel: string;
      imageKey: string | null;
      uom: DefaultUom;
      availableQty: number;
      qtyStr: string;
      priceStr: string;
      suggestedStr: string | null;
    };

let cartUid = 0;

/** The ticket's bouquets as the floor warning reads them (till and "Customer collected"). */
export function cartBouquetFloorLines(cart: CartLine[]): BouquetFloorLine[] {
  return cart.flatMap((line) =>
    line.kind === "BOUQUET"
      ? [{ label: line.label, price: Number(line.priceStr), floor: line.floor }]
      : [],
  );
}

/** One ticket line's money in cents, rounded as the server rounds it. */
export function cartLineCents(line: CartLine): number {
  const quantity = line.kind === "BOUQUET" ? 1 : Number(line.qtyStr) || 0;
  return lineCents(Number(line.priceStr), quantity);
}

/** The first thing a line still lacks, as a person reads it; null when all are complete. */
export function ticketLineProblem(cart: CartLine[], t: TranslateFn): string | null {
  for (const line of cart) {
    const price = Number(line.priceStr);
    if (line.priceStr.trim() === "" || !Number.isFinite(price) || price < 0) {
      return t("sale.setPriceFor", { label: line.label });
    }
    if (line.kind === "ITEM") {
      const qty = Number(line.qtyStr);
      if (!Number.isFinite(qty) || qty <= 0) return t("sale.setQuantityFor", { label: line.label });
      if (qty > line.availableQty) {
        return t("sale.onlyInStock", {
          qty: line.availableQty,
          unit: uomLabel(line.uom, line.availableQty),
          label: line.label,
        });
      }
    }
  }
  return null;
}

/** The ticket as sale lines, for the server. */
export function cartToSaleLines(cart: CartLine[]): CreateSaleLinePayload[] {
  return cart.map((line) =>
    line.kind === "BOUQUET"
      ? {
          kind: "BOUQUET",
          bouquetBuildId: line.bouquetBuildId,
          quantity: 1,
          unitPriceEur: Number(line.priceStr),
        }
      : {
          kind: "ITEM",
          itemId: line.itemId,
          quantity: Number(line.qtyStr),
          unitPriceEur: Number(line.priceStr),
        },
  );
}

/**
 * The cart's state and its edits. `onAdd` runs whenever something is added
 * (the till clears its last result there).
 */
export function useTicketCart(onAdd?: () => void) {
  const [cart, setCart] = useState<CartLine[]>([]);

  const addBouquet = (bouquet: FloorBouquetInStock, label: string) => {
    onAdd?.();
    setCart((previous) => [
      ...previous,
      {
        uid: ++cartUid,
        kind: "BOUQUET",
        bouquetBuildId: bouquet.id,
        label,
        priceStr: bouquet.suggestedSalePriceEur ?? "",
        suggestedStr: bouquet.suggestedSalePriceEur,
        floor: bouquetFloor(bouquet),
      },
    ]);
  };

  const addItem = useCallback(
    (item: FloorSellableItem) => {
      onAdd?.();
      setCart((previous) => {
        const existing = previous.find(
          (line): line is Extract<CartLine, { kind: "ITEM" }> =>
            line.kind === "ITEM" && line.itemId === item.id,
        );
        const availableQty = Number(item.qtyOnHand);
        if (existing) {
          const currentQty = Number(existing.qtyStr) || 0;
          if (Number.isFinite(availableQty) && currentQty >= availableQty) return previous;
          return previous.map((line) =>
            line.uid === existing.uid
              ? { ...line, qtyStr: String(currentQty + 1) }
              : line,
          );
        }
        return [
          ...previous,
          {
            uid: ++cartUid,
            kind: "ITEM",
            itemId: item.id,
            label: item.name,
            categoryCode: item.category.code,
            categoryLabel: item.category.label,
            imageKey: item.imageKey,
            uom: item.defaultUom,
            availableQty,
            qtyStr: "1",
            // G-45 Tier 2: prefill with the derived price when nobody typed one.
            priceStr: item.saleUnitPriceEur ?? "",
            suggestedStr: item.saleUnitPriceEur,
          },
        ];
      });
    },
    [onAdd],
  );

  const updateLine = (
    uid: number,
    patch: Partial<{ qtyStr: string; priceStr: string }>,
  ) => {
    setCart((previous) =>
      previous.map((line) => (line.uid === uid ? { ...line, ...patch } : line)),
    );
  };

  const removeLine = (uid: number) => {
    setCart((previous) => previous.filter((line) => line.uid !== uid));
  };

  /**
   * Owner ruling 2026-09-10: taking a line down to zero REMOVES it. Stepping
   * below one is the florist saying "not this after all", and the old code
   * both clamped the floor at 1 and disabled the minus button there, so the
   * only way out was the small × — or typing a 0 that then blocked checkout
   * with "Set a quantity".
   */
  const changeItemQuantity = (
    line: Extract<CartLine, { kind: "ITEM" }>,
    delta: number,
  ) => {
    const current = Number(line.qtyStr) || 0;
    const next = Math.min(line.availableQty, current + delta);
    if (next <= 0) {
      removeLine(line.uid);
      return;
    }
    updateLine(line.uid, { qtyStr: String(next) });
  };

  /**
   * A typed quantity is only judged when the field is LEFT: quantities may be
   * fractional (stems by weight step 0.001), so "0" is a legitimate keystroke
   * on the way to "0.5" and must not delete the line mid-typing.
   */
  const commitItemQuantity = (line: Extract<CartLine, { kind: "ITEM" }>) => {
    const qty = Number(line.qtyStr);
    if (line.qtyStr.trim() === "" || !Number.isFinite(qty) || qty <= 0) {
      removeLine(line.uid);
    }
  };

  // In cents, each line rounded as the server rounds it, so a card part
  // worked out from this total is the one the server agrees with (G-141).
  const totalCents = useMemo(
    () => cart.reduce((cents, line) => cents + cartLineCents(line), 0),
    [cart],
  );

  const cartedBuildIds = useMemo(
    () =>
      new Set(
        cart
          .filter((line): line is Extract<CartLine, { kind: "BOUQUET" }> =>
            line.kind === "BOUQUET",
          )
          .map((line) => line.bouquetBuildId),
      ),
    [cart],
  );

  const itemQtyInCart = useMemo(() => {
    const quantities = new Map<string, number>();
    for (const line of cart) {
      if (line.kind !== "ITEM") continue;
      quantities.set(line.itemId, Number(line.qtyStr) || 0);
    }
    return quantities;
  }, [cart]);

  return {
    cart,
    setCart,
    addBouquet,
    addItem,
    updateLine,
    removeLine,
    changeItemQuantity,
    commitItemQuantity,
    totalCents,
    cartedBuildIds,
    itemQtyInCart,
  };
}

export type TicketCart = ReturnType<typeof useTicketCart>;
